'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Smartphone, Globe, ShoppingCart, Blocks, Brain, Cloud, Wifi, Shield, ArrowRight, Sparkles } from 'lucide-react'
import Link from 'next/link'
import ConsultationModal from './modals/ConsultationModal'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'

function serviceSlug(url: string) {
  return url.split('/').pop() || ''
}

export default function Services() {
  const [showConsultationModal, setShowConsultationModal] = useState(false)

  const services = [
    {
      icon: <Smartphone className="w-7 h-7" />,
      title: "Mobile App Development",
      description: "Custom cross-platform mobile apps with seamless UI/UX and rigorous testing.",
      technologies: ["React Native", "Flutter", "Swift", "Kotlin"],
      color: "from-sky-400 to-blue-500",
      url: "/services/mobile-app-development"
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: "Web Development",
      description: "Expert web development from custom sites to complex web applications.",
      technologies: ["React", "Next.js", "Node.js", "WordPress"],
      color: "from-blue-400 to-indigo-500",
      url: "/services/web-development"
    },
    {
      icon: <ShoppingCart className="w-7 h-7" />,
      title: "eCommerce Development",
      description: "Scalable online stores and marketplaces with advanced payment integrations.",
      technologies: ["Shopify", "WooCommerce", "Magento", "Stripe"],
      color: "from-emerald-400 to-teal-500",
      url: "/services/ecommerce-development"
    },
    {
      icon: <Blocks className="w-7 h-7" />,
      title: "Blockchain Development",
      description: "Smart contracts, crypto wallets, NFT marketplaces, and DApps.",
      technologies: ["Solidity", "Web3.js", "Ethereum", "Polygon"],
      color: "from-orange-400 to-red-500",
      url: "/services/blockchain-development"
    },
    {
      icon: <Brain className="w-7 h-7" />,
      title: "AI & ML Services",
      description: "Intelligent automation, chatbots, and machine learning solutions.",
      technologies: ["TensorFlow", "PyTorch", "OpenAI", "Python"],
      color: "from-violet-400 to-purple-500",
      url: "/services/ai-ml-services"
    },
    {
      icon: <Cloud className="w-7 h-7" />,
      title: "DevOps & Cloud",
      description: "Cloud migration, CI/CD pipelines, and infrastructure optimization.",
      technologies: ["AWS", "Azure", "Docker", "Kubernetes"],
      color: "from-cyan-400 to-blue-500",
      url: "/services/devops-cloud"
    },
    {
      icon: <Wifi className="w-7 h-7" />,
      title: "IoT & Embedded",
      description: "Internet of Things solutions and smart device applications.",
      technologies: ["Arduino", "Raspberry Pi", "MQTT", "LoRa"],
      color: "from-teal-400 to-emerald-500",
      url: "/services/iot-embedded"
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Cybersecurity",
      description: "Security assessments, penetration testing, and protection services.",
      technologies: ["OWASP", "Nessus", "ISO 27001", "GDPR"],
      color: "from-slate-400 to-slate-600",
      url: "/services/cybersecurity"
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } }
  }

  return (
    <section id="services" className="py-20 sm:py-28 bg-gradient-to-b from-white via-sky-50/30 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-sky-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4"
          >
            <Sparkles className="w-4 h-4" />
            What We Do
          </motion.div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            Comprehensive digital solutions tailored to your business needs
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
        >
          {services.map((service, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Link 
                href={service.url}
                className="group block bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-sky-100/50 transition-all duration-500 border border-sky-100/50 hover:border-sky-200 hover:-translate-y-2 h-full relative overflow-hidden"
              >
                {/* Hover gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />

                {/* Service image with icon badge */}
                <div className="relative -mx-6 -mt-6 mb-5 h-36 overflow-hidden">
                  <SmartImage
                    src={siteImages.services[serviceSlug(service.url)] || siteImages.hero.secondary}
                    alt={service.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-60 mix-blend-multiply`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
                  <div className={`absolute bottom-3 left-6 inline-flex p-3 rounded-xl bg-gradient-to-br ${service.color} text-white shadow-lg group-hover:scale-110 transition-all duration-300`}>
                    {service.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Tech tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {service.technologies.slice(0, 3).map((tech, i) => (
                    <span key={i} className="px-2 py-0.5 bg-sky-50 text-sky-600 text-xs rounded-md font-medium">
                      {tech}
                    </span>
                  ))}
                  {service.technologies.length > 3 && (
                    <span className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded-md font-medium">
                      +{service.technologies.length - 3}
                    </span>
                  )}
                </div>

                {/* Arrow */}
                <div className="flex items-center text-sky-500 font-medium text-sm group-hover:text-sky-600 group-hover:translate-x-1 transition-all duration-300">
                  Learn More
                  <ArrowRight className="ml-1.5 w-4 h-4" />
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-8 lg:p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent)] pointer-events-none" />
            <h3 className="text-2xl font-bold mb-3 relative z-10">Ready to Start Your Project?</h3>
            <p className="text-sky-100 mb-6 max-w-2xl mx-auto relative z-10">
              Let our expert team bring your ideas to life with cutting-edge technology solutions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowConsultationModal(true)}
              className="bg-white text-sky-600 px-8 py-3 rounded-xl font-semibold hover:shadow-xl hover:shadow-white/20 transition-all duration-300 relative z-10"
            >
              Get Free Consultation
            </motion.button>
          </div>
        </motion.div>
      </div>

      <ConsultationModal 
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
      />
    </section>
  )
}
